import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuthStore } from '../stores/authStore';
import api from '../utils/api';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function SignUp() {
  const [step, setStep] = useState(1); // 1: Email verification, 2: Password setup, 3: OTP verification
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [otp, setOtp] = useState('');
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const projectIdFromQuery = new URLSearchParams(location.search).get('projectId');
  const projectIdFromPoster = location.state?.projectId ?? projectIdFromQuery;

  const handleEmailVerification = async (e) => {
    e.preventDefault();
    setError('');
    setAccountExists(false);
    
    if (!email || !email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setVerifyingEmail(true);

    try {
      const response = await api.post('/inductions/verify-invitation', {
        email: email.trim(),
        ...(projectIdFromPoster && { projectId: projectIdFromPoster })
      });
      
      if (response.data.success && response.data.isInvited) {
        setEmailVerified(true);
        setInvitationInfo(response.data.data);
        setFormData(prev => ({ ...prev, email: email.trim() }));
        setStep(2);
        setAccountExists(false);
        setError('');
        toast.success('Email verified! Please complete your registration.');
      } else {
        // Check if account already exists
        if (response.data.accountExists) {
          setAccountExists(true);
          setError(response.data.message || 'An account with this email already exists. Please log in instead.');
        } else {
          setAccountExists(false);
          setError(response.data.message || 'No invitation found for this email address. Please contact your project administrator.');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to verify email. Please try again.';
      setError(errorMessage);
      // Check if the error indicates account exists
      if (err.response?.data?.accountExists) {
        setAccountExists(true);
      } else {
        setAccountExists(false);
      }
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setSendingOTP(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await api.post('/auth/send-otp', registerData);
      
      if (response.data.success) {
        toast.success('OTP sent to your email address');
        setStep(3); // Move to OTP verification step
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(errorMessage);
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyingOTP(true);

    try {
      const response = await api.post('/auth/verify-otp', {
        email: formData.email,
        otp: otp
      });
      
      if (response.data.success) {
        const { user } = response.data.data;
        useAuthStore.setState({ user, isAuthenticated: true });
        
        toast.success('Registration successful!');
        navigate('/system-induction');
      } else {
        setError(response.data.message || 'Invalid OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid or expired OTP. Please try again.';
      setError(errorMessage);
    } finally {
      setVerifyingOTP(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Side - Sign Up Form */}
      <Box
        sx={{
          flex: { xs: '1', md: '0 0 45%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, md: 4 },
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 450, px: { xs: 2, md: 3 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 4, md: 6 }, justifyContent: 'center' }}>
            <Box
              component="img"
              src="/favicon.svg"
              alt="Galldris Core"
              sx={{ width: { xs: 40, md: 50 }, height: { xs: 40, md: 50 }, mr: 2 }}
            />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
              Galldris Core
            </Typography>
          </Box>

          {/* Header */}
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              {step === 1 ? 'Sign Up' : step === 2 ? 'Complete Your Registration' : 'Verify Your Email'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {step === 1 
                ? 'Enter the email address you received the invitation on'
                : step === 2
                ? 'Set up your account password and personal information'
                : 'Enter the 6-digit code sent to your email'
              }
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => {
                setError('');
                setAccountExists(false);
              }}
            >
              {error}
              {accountExists && (
                <Box sx={{ mt: 1 }}>
                  <MuiLink
                    component={Link}
                    to="/login"
                    underline="hover"
                    sx={{ 
                      color: 'error.main',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'error.dark'
                      }
                    }}
                  >
                    Click here to log in
                  </MuiLink>
                </Box>
              )}
            </Alert>
          )}

          {/* Step 1: Email Verification */}
          {step === 1 && (
            <Box component="form" onSubmit={handleEmailVerification}>
              <TextField
                fullWidth
                placeholder="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={verifyingEmail}
                sx={{ mb: 3 }}
                variant="outlined"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={verifyingEmail}
                sx={{ 
                  py: 1.5, 
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                {verifyingEmail ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Verifying...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>

              {/* Login Link */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Already have an account?{' '}
                  <MuiLink
                    component={Link}
                    to="/login"
                    underline="hover"
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.dark'
                      }
                    }}
                  >
                    Log in
                  </MuiLink>
                </Typography>
              </Box>
            </Box>
          )}

          {/* Step 2: Password Setup */}
          {step === 2 && (
            <>
              {/* Email Verified Success Message */}
              {emailVerified && invitationInfo && (
                <Alert 
                  icon={<CheckCircleIcon />} 
                  severity="success" 
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Email verified for: {invitationInfo.projectName}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Project: {invitationInfo.projectNumber}
                  </Typography>
                </Alert>
              )}

              <Box component="form" onSubmit={handleSendOTP}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    name="first_name"
                    placeholder="First Name *"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    autoFocus
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    name="last_name"
                    placeholder="Last Name *"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Box>

                <TextField
                  fullWidth
                  name="email"
                  placeholder="Email"
                  type="email"
                  value={formData.email}
                  disabled
                  sx={{ mb: 2 }}
                  variant="outlined"
                />
                
                <TextField
                  fullWidth
                  name="password"
                  placeholder="Password *"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 2 }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((prev) => !prev)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  placeholder="Confirm Password *"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={sendingOTP}
                  sx={{ 
                    py: 1.5, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {sendingOTP ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </Button>

                {/* Back to email step */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setEmailVerified(false);
                      setError('');
                    }}
                    underline="hover"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    ← Use a different email
                  </MuiLink>
                </Box>
              </Box>
            </>
          )}

          {/* Step 3: OTP Verification */}
          {step === 3 && (
            <>
              <Alert 
                icon={<CheckCircleIcon />} 
                severity="info" 
                sx={{ mb: 3 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  OTP sent to: {formData.email}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Please check your email and enter the 6-digit code
                </Typography>
              </Alert>

              <Box component="form" onSubmit={handleVerifyOTP}>
                <TextField
                  fullWidth
                  placeholder="Enter 6-digit OTP"
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                  }}
                  required
                  autoFocus
                  inputProps={{
                    maxLength: 6,
                    style: { 
                      textAlign: 'center',
                      fontSize: '24px',
                      letterSpacing: '8px',
                      fontFamily: 'monospace'
                    }
                  }}
                  sx={{ mb: 3 }}
                  variant="outlined"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={verifyingOTP || otp.length !== 6}
                  sx={{ 
                    py: 1.5, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {verifyingOTP ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Create Account'
                  )}
                </Button>

                {/* Resend OTP */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Didn't receive the code?
                  </Typography>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={async () => {
                      setError('');
                      setSendingOTP(true);
                      try {
                        const { confirmPassword, ...registerData } = formData;
                        const response = await api.post('/auth/send-otp', registerData);
                        if (response.data.success) {
                          toast.success('OTP resent to your email');
                        }
                      } catch (err) {
                        setError('Failed to resend OTP. Please try again.');
                      } finally {
                        setSendingOTP(false);
                      }
                    }}
                    disabled={sendingOTP}
                    underline="hover"
                    sx={{ 
                      color: 'primary.main',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        color: 'primary.dark'
                      },
                      '&:disabled': {
                        color: 'text.disabled',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {sendingOTP ? 'Sending...' : 'Resend OTP'}
                  </MuiLink>
                </Box>

                {/* Back to password step */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => {
                      setStep(2);
                      setOtp('');
                      setError('');
                    }}
                    underline="hover"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      '&:hover': {
                        color: 'primary.main'
                      }
                    }}
                  >
                    ← Back to password setup
                  </MuiLink>
                </Box>
              </Box>
            </>
          )}

          {/* Footer Links */}
          <Box sx={{ mt: { xs: 4, md: 6 }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 2, md: 3 } }}>
            <MuiLink component={Link} to="/privacy-policy" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Privacy Policy
            </MuiLink>
            <MuiLink component={Link} to="/support" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Support
            </MuiLink>
            <MuiLink component={Link} to="/cookie-policy" underline="none" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Cookie Policy
            </MuiLink>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: { xs: 3, md: 4 }, textAlign: 'center', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
            Copyright © 2026 Galldris Core. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Hero Image */}
      <Box
        sx={{
          flex: '0 0 55%',
          position: 'relative',
          backgroundImage: 'url(/images/construction-aerial.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'flex-end',
          overflow: 'hidden'
        }}
      >
        {/* Gradient Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(17,87,64,0.85))'
          }}
        />

        {/* Modern Text Overlay */}
        <Box
          sx={{
            position: 'relative',
            p: 6,
            zIndex: 1
          }}
        >
          {/* Date Badge */}
          <Box 
            sx={{ 
              display: 'inline-block',
              bgcolor: 'rgba(248, 187, 0, 0.9)',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 1,
              mb: 3,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              boxShadow: '0 4px 12px rgba(248, 187, 0, 0.3)'
            }}
          >
            {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </Box>

          {/* Main Headline */}
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 900, 
              color: 'white',
              mb: 3,
              lineHeight: 1.2,
              textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              letterSpacing: -0.5
            }}
          >
            Join Galldris Core Today
          </Typography>
          
          {/* Description */}
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255,255,255,0.95)',
              mb: 4,
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: '90%',
              textShadow: '0 1px 10px rgba(0,0,0,0.5)'
            }}
          >
            Start managing your construction projects efficiently with our comprehensive platform.
          </Typography>

          {/* Feature Tags */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>⚡</Box>
              Offline Support
            </Box>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>🔄</Box>
              Real-time Sync
            </Box>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', 
              px: 3, 
              py: 1.5, 
              borderRadius: 2, 
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>📱</Box>
              Mobile Ready
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
